import axios from 'axios'
import { clearActivity, isSessionIdle } from '@/lib/session-activity'
import {
    clearAccessToken,
    getAccessToken,
    setAccessToken,
} from '@/lib/access-token-store'
import {
    authStorage,
    authTabHeaders,
    clearTabSessionMark,
    getAuthTabId,
    isTabSession,
    renewAuthTabId,
} from '@/lib/auth-tab-session'

/**
 * Renovação silenciosa da sessão, compartilhada pelo cliente HTTP e pelo
 * socket.
 *
 * Antes, um access token expirado derrubava o usuário no meio do trabalho: o
 * interceptor só sabia limpar a sessão, e o socket deslogava ao receber
 * TOKEN_EXPIRED. Com a renovação, o tempo de vida do access token deixa de ser
 * um problema de fluxo e vira o que deveria ser — uma janela curta de
 * exposição.
 */
export const AUTH_STORAGE_KEY = 'atendimento_auth'
export const TOKEN_EXPIRATION_KEY = 'token_expiration'

export const readStoredAuth = (): {
    token?: string
    refreshToken?: string
} | null => {
    if (typeof window === 'undefined') return null

    try {
        const authData = authStorage().getItem(AUTH_STORAGE_KEY)
        return authData ? JSON.parse(authData) : null
    } catch (error) {
        console.error('Erro ao ler a sessão do localStorage:', error)
        return null
    }
}

/**
 * Cabeçalho com que o painel pede o modo cookie: o backend então devolve o
 * refresh num cookie httpOnly em vez de no corpo da resposta.
 */
export const AUTH_CLIENT_HEADER = 'X-Auth-Client'
export const WEB_AUTH_CLIENT = 'web'

/** O access token vive em memória; o localStorage não guarda mais token algum. */
export const getStoredToken = (): string | null => getAccessToken()

export const clearStoredSession = () => {
    clearAccessToken()
    // Aba de usuário de IA limpa só a sessão dela (ver `auth-tab-session.ts`).
    if (isTabSession()) {
        authStorage().removeItem(AUTH_STORAGE_KEY)
        authStorage().removeItem(TOKEN_EXPIRATION_KEY)
        clearTabSessionMark()
        return
    }
    localStorage.removeItem(AUTH_STORAGE_KEY)
    localStorage.removeItem(TOKEN_EXPIRATION_KEY)
    clearActivity()
}

/**
 * Canal entre as abas do painel.
 *
 * O refresh vive num cookie compartilhado por todas elas e é rotacionado a cada
 * uso, mas o access token é de cada aba. Sem este canal, cada aba renovava por
 * conta própria assim que o próprio token vencia; as que saíssem no mesmo
 * instante apresentavam o mesmo refresh, e o backend derrubava a sessão de
 * todas. Anunciando a renovação, uma aba renova e as demais só aproveitam.
 *
 * `BroadcastChannel` não existe em todo navegador (nem em teste): sem ele, cada
 * aba segue renovando sozinha, como antes.
 *
 * Um canal por SESSÃO: o da sessão normal e um para cada aba de usuário de IA. A aba da IA
 * não pode aproveitar o token renovado pela sessão normal (é outro usuário), nem o contrário.
 * Como a aba pode mudar de sessão (entrar com a IA numa aba que era normal), o canal é
 * resolvido a cada uso.
 */
const AUTH_CHANNEL_NAME = 'mediardex_auth'

const hasBroadcastChannel = typeof window !== 'undefined' && 'BroadcastChannel' in window

let authChannel: BroadcastChannel | null = null
let authChannelName: string | null = null

const currentAuthChannelName = (): string =>
    isTabSession() ? `${AUTH_CHANNEL_NAME}_tab_${getAuthTabId()}` : AUTH_CHANNEL_NAME

const getAuthChannel = (): BroadcastChannel | null => {
    if (!hasBroadcastChannel) return null
    const name = currentAuthChannelName()
    if (authChannel && authChannelName === name) return authChannel
    authChannel?.close()
    authChannel = new BroadcastChannel(name)
    authChannelName = name
    authChannel.onmessage = handleAuthChannelMessage
    return authChannel
}

type AuthChannelMessage = { type: 'token-renewed'; token: string }

const handleAuthChannelMessage = async (event: MessageEvent<AuthChannelMessage>) => {
    // A aba mudou de sessão depois que este canal abriu: o anúncio é de OUTRO usuário.
    if (event.target !== authChannel || authChannelName !== currentAuthChannelName()) {
        getAuthChannel()
        return
    }

    const message = event.data

    if (message?.type !== 'token-renewed' || !message.token) return
    if (getAccessToken() === message.token) return

    // Aba que já saiu não volta a entrar pelo anúncio de outra: sem esta
    // guarda, um logout aqui seria desfeito pela renovação de lá.
    if (
        (window as any).__atendimento_logging_out ||
        !authStorage().getItem(AUTH_STORAGE_KEY)
    ) {
        return
    }

    setAccessToken(message.token)

    // Mesmo import dinâmico de `requestNewAccessToken`, e pelo mesmo motivo:
    // o store importa os módulos de API, e um import estático fecharia o ciclo.
    const [{ store }, { updateTokens }] = await Promise.all([
        import('@/store/store'),
        import('@/store/slices/authSlice'),
    ])

    store.dispatch(updateTokens({ token: message.token }))
}

// Abre já o canal da sessão desta aba, para ouvir as renovações das outras.
getAuthChannel()

/**
 * "Duplicar aba" copia o `sessionStorage`: a cópia nasceria com o mesmo id e a mesma sessão
 * de usuário de IA, e as duas renovariam o mesmo cookie (o backend derruba a sessão quando o
 * refresh é reapresentado). Ao carregar, a aba de IA se anuncia; se outra responder com o
 * mesmo id, esta é a cópia: ganha id novo, larga a sessão e vai para o login.
 */
const TAB_PRESENCE_CHANNEL = 'mediardex_tab_presence'

type TabPresenceMessage =
    | { type: 'hello'; tabId: string; nonce: string }
    | { type: 'taken'; tabId: string; nonce: string }

type TabPresenceState = { channel: BroadcastChannel; nonces: Set<string> }

if (hasBroadcastChannel) {
    // Um ouvinte por PÁGINA. O módulo pode rodar de novo na mesma aba (HMR do Vite, em
    // desenvolvimento): a cópia antiga continuava ouvindo e respondia "este id é meu" ao
    // anúncio da nova, que se achava duplicada e ia para o login. O canal antigo é fechado,
    // e os anúncios desta página (todas as cópias) nunca são respondidos por ela mesma.
    const pageState = (window as any).__mediardexTabPresence as TabPresenceState | undefined
    pageState?.channel.close()
    const ownNonces = pageState?.nonces ?? new Set<string>()

    const presence = new BroadcastChannel(TAB_PRESENCE_CHANNEL)
    const nonce = Math.random().toString(36).slice(2)
    ownNonces.add(nonce)
    ;(window as any).__mediardexTabPresence = { channel: presence, nonces: ownNonces } satisfies TabPresenceState

    presence.onmessage = (event: MessageEvent<TabPresenceMessage>) => {
        const message = event.data
        const tabId = getAuthTabId()
        if (!message || !tabId || message.tabId !== tabId) return

        if (message.type === 'hello' && !ownNonces.has(message.nonce) && isTabSession()) {
            presence.postMessage({ type: 'taken', tabId, nonce: message.nonce })
            return
        }

        if (message.type === 'taken' && message.nonce === nonce) {
            authStorage().removeItem(AUTH_STORAGE_KEY)
            authStorage().removeItem(TOKEN_EXPIRATION_KEY)
            clearTabSessionMark()
            clearAccessToken()
            renewAuthTabId()
            window.location.assign('/login')
        }
    }

    const tabId = getAuthTabId()
    if (tabId && isTabSession()) presence.postMessage({ type: 'hello', tabId, nonce })
}

const requestNewAccessToken = async (): Promise<string | null> => {
    // Sem isto, a sessão seria eterna: a aplicação tem polls (notificações,
    // badges, socket) que continuam pedindo dados com a aba esquecida aberta, e
    // cada 401 renovaria o token sem ninguém na frente da tela.
    // Aba de usuário de IA não tem ninguém clicando: ela fica logada esperando
    // conversa, e quem a encerra é o supervisor (sair) ou o fim do refresh.
    if (!isTabSession() && isSessionIdle()) return null

    const baseURL = import.meta.env.VITE_API_BASE_URL

    // Corpo vazio: quem carrega o refresh é o cookie httpOnly, enviado pelo
    // navegador. `withCredentials` é o que faz o cookie viajar entre o painel e
    // a API. axios "cru" — passar pelo cliente normal reentraria no interceptor.
    const { data } = await axios.post(
        `${baseURL}/auth/refresh`,
        {},
        {
            headers: {
                'Content-Type': 'application/json',
                [AUTH_CLIENT_HEADER]: WEB_AUTH_CLIENT,
                // Aba de usuário de IA renova pelo cookie dela (ver `auth-tab-session.ts`).
                ...authTabHeaders(),
            },
            withCredentials: true,
        }
    )

    const accessToken: string | undefined = data?.access_token
    if (!accessToken) return null

    setAccessToken(accessToken)

    // Import dinâmico: o store importa slices que importam os módulos de API, e
    // um import estático fecharia o ciclo.
    const [{ store }, { updateTokens }] = await Promise.all([
        import('@/store/store'),
        import('@/store/slices/authSlice'),
    ])

    store.dispatch(updateTokens({ token: accessToken, user: data?.user }))

    // As outras abas aproveitam esta renovação em vez de disputarem o mesmo
    // refresh (que já foi rotacionado e não vale mais).
    getAuthChannel()?.postMessage({ type: 'token-renewed', token: accessToken })

    return accessToken
}

/**
 * Uma renovação por vez: várias requisições que falham juntas esperam a mesma
 * promise em vez de dispararem N chamadas concorrentes ao `/auth/refresh`.
 */
let refreshInFlight: Promise<string | null> | null = null

/**
 * Renova a sessão.
 *
 * `staleToken` é a credencial que a requisição usou e levou a recusa. Quando ela
 * já não é a que está em memória, alguém renovou nesse meio tempo — outra
 * requisição desta aba ou outra aba — e a resposta certa é reaproveitar o token
 * novo, não pedir mais um.
 *
 * Sem isso, toda requisição que saía com o token velho e voltava DEPOIS do fim
 * de uma renovação disparava outra: o `/auth/refresh` rotaciona o refresh a cada
 * uso, e a cascata terminava em duas chamadas simultâneas apresentando o mesmo
 * token — o que o backend lê, com razão, como cópia.
 */
export const refreshAccessToken = (
    staleToken?: string | null
): Promise<string | null> => {
    if (staleToken) {
        const current = getAccessToken()
        if (current && current !== staleToken) return Promise.resolve(current)
    }

    if (!refreshInFlight) {
        refreshInFlight = requestNewAccessToken()
            .catch(() => null)
            .finally(() => {
                refreshInFlight = null
            })
    }

    return refreshInFlight
}

/**
 * Encerra a sessão também no servidor.
 *
 * Sair apagando só o localStorage deixava o refresh válido até expirar: quem
 * tivesse uma cópia continuava renovando depois do "sair". O `keepalive` é o que
 * garante a entrega — sem ele o navegador cancela a requisição no meio, porque
 * logo em seguida a página navega para a tela de login.
 *
 * Best-effort de propósito: sair nunca pode travar por causa da rede.
 */
export const endServerSession = async (): Promise<void> => {
    const token = getStoredToken()
    const baseURL = import.meta.env.VITE_API_BASE_URL

    const headers: Record<string, string> = {
        [AUTH_CLIENT_HEADER]: WEB_AUTH_CLIENT,
        // Aba de usuário de IA encerra só a sessão dela.
        ...authTabHeaders(),
    }

    if (token) headers.Authorization = `Bearer ${token}`

    try {
        // Vai mesmo sem access token em mãos: o cookie identifica a sessão, e é
        // ele que precisa ser revogado e apagado.
        await fetch(`${baseURL}/auth/logout`, {
            method: 'POST',
            headers,
            credentials: 'include',
            keepalive: true,
        })
    } catch {
        // Sem rede: a sessão local cai de qualquer forma.
    }
}
